import classNames from "classnames";
import { Controller } from "react-hook-form";
import Icon from "./icon";
import FileInput from "./file-input";
import FlatpickrInput from "./flatpickr-input";
import Translate from "./translate";
import DropdownQuestion from "./dropdown-question";
import RadioCheckboxAnswers from "./radio-checkbox-answers";
import UserLocationAutocompleteQuestion from "./user-location-autocomplete-question";
import TextNumberInput from "./text-number-input";
import CustomCheckbox from "./custom-checkbox";

const inputMap = {
  "Short answer": "text",
  "Personal Data": "text",
  Number: "number",
  Paragraph: "textarea",
  "Radio Self Submit": "radio",
  "Multiple choice": "radio", //not multiple
  "Checkboxes Self Submit": "checkbox",
  Checkboxes: "checkbox",
  "Custom-checkbox": "custom-checkbox",
  Dropdown: "select",
  "User Location": "user-location-autocomplete",
  "File upload": "file",
  "Date String": "date-string",
  Date: "date",
  Datetime: "datetime",
  Time: "time",
};

const Question = ({
  question,
  error,
  touched,
  register,
  trigger,
  isDirty,
  control,
  readOnlyMode,
  labels,
  pattern,
  customPatternErrorMsg,
  attachments,
  value,
  comment,
  isInline = false,
  minLength,
  maxLength,
  maxNAnswers,
  locale,
  trim,
  customPh,
  onFocus,
}) => {
  const type = inputMap[question?.questionType];
  const name = `${question.id}`;
  const required = question?.minNAnswers > 0;
  const errorMsg = labels?.THIS_FIELD_IS_MANDATORY || "This field is mandatory";
  const patternErrorMsg =
    customPatternErrorMsg || labels?.INVALID_VALUE || "The inserted value is not valid";
  const feedback = error ? "error" : touched ? "ok" : false;
  const isSelected = (answer) =>
    type === "radio" || (type === "custom-checkbox" && maxNAnswers === 1)
      ? Number(value) === answer.id
      : value && Array.isArray(value)
      ? value.map((x) => Number(x)).includes(answer.id)
      : type === "checkbox" || (type === "custom-checkbox" && maxNAnswers > 1)
      ? value && Array.isArray(value)
        ? value.map((x) => Number(x)).includes(answer.id)
        : Number(value) === answer.id
      : false;

  return type ? (
    <div
      className={classNames(
        `form__input-wrapper form__input-wrapper--${
          type === "custom-checkbox" ? "checkbox" : type
        }`,
        {
          "form__input-wrapper--error": feedback === "error",
          "form__input-wrapper--ok": feedback === "ok",
          "form__input-wrapper--small": [
            "number",
            "date",
            "datetime",
            "time",
            "date-string",
          ].includes(type),
        }
      )}
      data-id={question.id}
    >
      <label className="form__input-label" htmlFor={name}>
        <Translate text={question.label} />
        {required && <span className="form__required-symbol">*</span>}
      </label>
      <div className="form__input-box">
        {["checkbox", "radio"].includes(type) || (type === "custom-checkbox" && maxNAnswers > 1) ? (
          <div
            className={classNames(
              `form__${type === "custom-checkbox" ? "checkbox" : type}`,
              isInline && `form__${type === "custom-checkbox" ? "checkbox" : type}--inline`
            )}
          >
            {question.answers?.map((answer, i) => (
              <div
                className={classNames(
                  `form__${type === "custom-checkbox" ? "checkbox" : type}-wrapper`,
                  isInline &&
                    `form__${type === "custom-checkbox" ? "checkbox" : type}-wrapper--border`,
                  isInline &&
                    isSelected(answer) &&
                    `form__${type === "custom-checkbox" ? "checkbox" : type}-wrapper--selected`
                )}
                key={answer.id}
              >
                <input
                  {...register(name, { required: { value: required, message: errorMsg } })}
                  type={type === "custom-checkbox" ? "checkbox" : type}
                  className={classNames(
                    `form__input form__input--${type === "custom-checkbox" ? "checkbox" : type}`,
                    isDirty && "form__input--dirty"
                  )}
                  id={`${name}-${answer.id}`}
                  value={answer.id}
                  disabled={
                    readOnlyMode ||
                    (maxNAnswers
                      ? value &&
                        !value?.includes(answer.id.toString()) &&
                        value?.length >= maxNAnswers
                      : false)
                  }
                />
                <RadioCheckboxAnswers
                  type={type === "custom-checkbox" ? "checkbox" : type}
                  name={name}
                  required={required}
                  answer={answer}
                  attachments={attachments}
                />
              </div>
            ))}
          </div>
        ) : type === "custom-checkbox" && maxNAnswers === 1 ? (
          <CustomCheckbox
            value={value}
            question={question}
            control={control}
            isInline={isInline}
            isSelected={isSelected}
            isDirty={isDirty}
            readOnlyMode={readOnlyMode}
            required={required}
            errorMsg={errorMsg}
            attachments={attachments}
            name={name}
          />
        ) : ["text", "number"].includes(type) ? (
          <TextNumberInput
            control={control}
            name={name}
            required={required}
            errorMsg={errorMsg}
            pattern={pattern}
            patternErrorMsg={patternErrorMsg}
            type={type}
            isDirty={isDirty}
            labels={labels}
            readOnlyMode={readOnlyMode}
            minLength={minLength}
            maxLength={maxLength}
            trim={trim}
            customPh={customPh}
            onFocus={onFocus}
          />
        ) : type === "textarea" ? (
          <textarea
            {...register(name, { required: { value: required, message: errorMsg } })}
            type={type}
            className={classNames(
              `form__input form__input--${type}`,
              isDirty && "form__input--dirty"
            )}
            placeholder={
              customPh ||
              labels?.[`WELFARE_${type.toUpperCase()}_PLACEHOLDER`] ||
              "Type your answer"
            }
            disabled={readOnlyMode}
          />
        ) : type === "file" ? (
          <Controller
            name={name}
            control={control}
            rules={{ required }}
            render={({ field }) => (
              <FileInput
                name={name}
                field={field}
                feedback={feedback}
                required={required}
                maxFiles={question?.maxNAnswers}
                disabled={readOnlyMode}
                labels={labels}
                trigger={trigger}
              />
            )}
          />
        ) : type === "select" ? (
          <DropdownQuestion
            name={name}
            answers={question.answers}
            control={control}
            required={required}
            labels={labels}
            readOnlyMode={readOnlyMode}
            customPh={customPh}
          />
        ) : type === "user-location-autocomplete" ? (
          <UserLocationAutocompleteQuestion
            name={name}
            control={control}
            required={required}
            labels={labels}
            readOnlyMode={readOnlyMode}
            customPh={customPh}
          />
        ) : ["date-string", "date", "datetime", "time"].includes(type) ? (
          <FlatpickrInput
            type={type}
            name={name}
            feedback={feedback}
            required={required}
            control={control}
            disabled={readOnlyMode}
            labels={labels}
            placeholder={
              customPh ||
              labels?.[`WELFARE_${type.toUpperCase()}_PLACEHOLDER`] ||
              "Enter your answer"
            }
            toggleLabel={labels?.[`WELFARE_${type.toUpperCase()}_TOGGLE`] || "Toggle calendar"}
            locale={locale}
          />
        ) : null}
        <span className="form__input-button">
          {feedback === "error" ? <Icon name="error" /> : <Icon name="check" />}
        </span>
      </div>
      {comment && (
        <p className={classNames("form__comment", comment?.className)}>{comment?.message}</p>
      )}
      {error && <p className="form__error-msg">{error?.message || errorMsg}</p>}
    </div>
  ) : null;
};

export default Question;
